import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Users, BedDouble, Gauge, Images } from "lucide-react";
import { Listing, Kind, mediaUrl, money, metaFor } from "@/lib/api";

export const ListingImage = ({ src, alt, className = "" }: { src?: string; alt: string; className?: string }) =>
  src ? (
    <img src={mediaUrl(src)} alt={alt} loading="lazy" className={`h-full w-full object-cover ${className}`} />
  ) : (
    <div className={`flex h-full w-full items-center justify-center bg-gradient-to-br from-navy/5 to-gold/10 font-mono text-[10px] uppercase tracking-widest text-navy/40 ${className}`}>Photo coming soon</div>
  );

export const ListingCard = ({ item, kind, i = 0 }: { item: Listing; kind: Kind; i?: number }) => {
  const to = `/${kind === "stay" ? "stays" : "vehicles"}/${item.id}`;
  const cover = item.images?.[0];
  const tag = kind === "stay" ? item.category : item.vehicle_class;
  return (
    <Link to={to} data-testid={`listing-card-${item.id}`} className="group block fade-up" style={{ animationDelay: `${i * 70}ms` }}>
      <div className="relative aspect-[4/3] overflow-hidden rounded-2xl bg-white">
        <ListingImage src={cover} alt={metaFor(item, cover)?.alt || item.title} className="transition-transform duration-700 group-hover:scale-[1.04]" />
        {tag && <span className="absolute left-3 top-3 rounded-full bg-navy/80 px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.18em] text-brass backdrop-blur">{tag}</span>}
        {(item.images?.length || 0) > 1 && <span className="absolute bottom-3 right-3 flex items-center gap-1 rounded-full bg-black/55 px-2 py-0.5 text-[11px] text-white backdrop-blur"><Images size={12} /> {item.images!.length}</span>}
        {item.status === "unavailable" && <span className="absolute right-3 top-3 rounded-full bg-white/90 px-2.5 py-1 text-[11px] font-semibold text-navy">Temporarily unavailable</span>}
      </div>
      <div className="mt-4 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="truncate font-sans text-base font-semibold text-navy">{item.title}</h3>
          <p className="mt-1 flex items-center gap-1.5 text-sm text-navy/55"><MapPin size={13} /> {[item.area, item.city].filter(Boolean).join(", ")}</p>
          <p className="mt-1 flex items-center gap-3 text-xs text-navy/50">
            {kind === "stay" ? (
              <>{item.bedrooms ? <span className="flex items-center gap-1"><BedDouble size={13} /> {item.bedrooms} bd</span> : null}{item.capacity ? <span className="flex items-center gap-1"><Users size={13} /> {item.capacity} guests</span> : null}</>
            ) : (
              <>{item.capacity ? <span className="flex items-center gap-1"><Users size={13} /> {item.capacity} seats</span> : null}{item.transmission ? <span className="flex items-center gap-1"><Gauge size={13} /> {item.transmission}</span> : null}</>
            )}
          </p>
        </div>
        <p className="shrink-0 text-right" data-testid={`listing-rate-${item.id}`}>
          {item.rate ? (<><span className="font-mono text-base font-bold">{money(item.rate, item.currency)}</span><span className="block text-xs text-navy/50">per {kind === "stay" ? "night" : "day"}</span></>)
            : <span className="text-xs font-semibold text-brass-deep">Rate on request</span>}
        </p>
      </div>
    </Link>
  );
};
